import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { accountingApi, formatInr } from "@/api/accounting";
import { useAccountingHospitalContext } from "./AccountingHospitalContext";

function defaultMonthRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return {
    from: `${y}-${m}-01`,
    to: `${y}-${m}-${String(last).padStart(2, "0")}`,
  };
}

export default function AccountingCfoPage() {
  const { hospitalId } = useAccountingHospitalContext();
  const defaults = defaultMonthRange();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);

  const q = useQuery({
    queryKey: ["accounting-books-kpis", hospitalId, fromDate, toDate],
    queryFn: () => accountingApi.booksKpis(hospitalId, { fromDate, toDate }),
    enabled: !!hospitalId && !!fromDate && !!toDate,
  });

  const currency = (q.data?.kpis ?? []).filter(
    (k) => k.unit === "CURRENCY_INR" || k.unit === "PERCENT"
  );

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium">CFO books KPIs</h2>
        <p className="mt-1 text-sm text-slate-500">
          Deterministic KPIs from posted double-entry books. Ops Finance Intelligence stays
          primary — use{" "}
          <Link className="underline" to="../statements">
            Statements
          </Link>{" "}
          for P&amp;L / BS / CF detail.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="w-44 space-y-1">
          <Label className="text-xs">From</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div className="w-44 space-y-1">
          <Label className="text-xs">To</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
      </div>

      {q.data?.note ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {q.data.note}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-4 text-sm">
        <span>
          Posted vouchers:{" "}
          <strong>{q.data?.coverage.postedVoucherCount ?? "—"}</strong>
        </span>
        <span>
          Ready:{" "}
          <strong>
            {q.data?.coverage.readyForIntelligence ? "Yes" : "No — post journals first"}
          </strong>
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {currency.map((k) => (
          <div key={k.key} className="rounded-md border border-slate-200 px-3 py-2">
            <div className="text-xs text-slate-500">{k.label}</div>
            <div className="text-lg tabular-nums font-medium">
              {k.unit === "PERCENT" ? `${k.value}%` : formatInr(k.value)}
            </div>
          </div>
        ))}
      </div>

      {!q.isLoading && !(q.data?.kpis ?? []).length ? (
        <p className="text-sm text-slate-500">
          No books KPIs yet. Seed CoA and ensure ops→books posting or opening balances.
        </p>
      ) : null}

      {(q.data?.byHospital ?? []).length > 1 ? (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hospital</TableHead>
                <TableHead className="text-right">Posted vouchers</TableHead>
                <TableHead>Has books</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.data!.byHospital.map((h) => (
                <TableRow key={h.hospitalId}>
                  <TableCell>{h.hospitalName}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {h.postedVoucherCount}
                  </TableCell>
                  <TableCell>{h.hasBooks ? "Yes" : "No"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}
